import React, { FocusEvent } from 'react';
import { Form } from 'react-bootstrap';
import { InputFieldState } from '../types';
import options from './options.json'

/**
 * PaymentFormコンポーネント用のPropsです。
 * @interface PaymentFormProps
 */
interface PaymentFormProps {

  /**
   * 納付方法
   * @type {InputFieldState}
   * @memberof PaymentFormProps
   */
  paymentMethod: InputFieldState;

  /**
   * 納付方法を設定する関数
   * @memberof PaymentFormProps
   */
  setPaymentMethod: (paymentMethod: InputFieldState) => void;

  /**
   * 納付番号
   * @type {InputFieldState}
   * @memberof PaymentFormProps
   */
  paymentNumber: InputFieldState;

  /**
   * 納付番号を設定する関数
   * @memberof PaymentFormProps
   */
  setPaymentNumber: (paymentNumber: InputFieldState) => void;

}

/**
 * 納付フォームのコンポーネントです。
 * @param {string} props.paymentMethod - 表示する納付方法
 * @param {function} props.setPaymentMethod - 納付方法が変更されたときに呼ばれるコールバック関数
 * @param {string} props.paymentNumber - 表示する納付番号
 * @param {function} props.setPaymentNumber - 納付番号が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 納付フォームのJSX要素
 */
const PaymentForm: React.FC<PaymentFormProps> = ({ paymentMethod, setPaymentMethod, paymentNumber, setPaymentNumber }) => {

  /**
   * フォームからフォーカスが外れたときのイベントハンドラーです。
   * 入力された納付用番号を整形し、onChange関数を呼び出します。
   * @param {object} event - フォーカスアウトのイベント
   * @returns {void}
   */
    const handleBlur = (event: FocusEvent<HTMLInputElement>): void => {
      const halfWidthText = event.target.value.replace(/[Ａ-Ｚａ-ｚ０-９]/g, function(s) {
        return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
      });

      // 予納台帳番号は６桁、振替番号は８桁
      let formatPaymentNumber = '';
      let errorMessage = '';
      options.forEach((option) => {
        if(option.value === paymentMethod.value) {
          const deleteNonNumberInputValue = halfWidthText.replace(/[^0-9]/g, '').substring(0, option.numberOfDigit);
          formatPaymentNumber = deleteNonNumberInputValue ? ('0'.repeat(option.numberOfDigit) + deleteNonNumberInputValue).slice(-1 * option.numberOfDigit) : deleteNonNumberInputValue;
          const regexp = new RegExp("^\\d{" + option.numberOfDigit.toString() + "}$");
          errorMessage = halfWidthText === '' ? `${paymentMethod.value.replace('口座', '')}番号が入力されていません。` : (!regexp.test(formatPaymentNumber) ? `入力された${paymentMethod.value.replace('口座', '')}番号の形式（${option.numberOfDigit.toString()}）が正しくありません。。` : '');
        }
      });
      setPaymentNumber({
        value: formatPaymentNumber,
        errorMessage: errorMessage
      });
    }

  /**
   * フォームからフォーカスが外れたときのイベントハンドラーです。
   * @param {object} event - フォーカスアウトのイベント
   * @returns {void}
   */
  const handleMethodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setPaymentMethod({
      value: value,
      errorMessage: value ? '' : '納付方法が選択されていません。'
    });
  };

  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setPaymentNumber({
      value: value,
      errorMessage: paymentNumber.errorMessage
    });
  };

  return (
    <Form.Group className="mb-3 row">
      <Form.Label className="col-lg-2 col-12">
        納付方法
        <span className="text-small text-danger ms-3">※必須</span>
      </Form.Label>
      <div className="col-lg-10 col-12">
        <div className="row">
          <div className="col-4">
            <Form.Select onChange={handleMethodChange} aria-label="納付方法" value={paymentMethod.value}>
              {
                options.map((option, index) => {
                  return <option key={index} value={option.value}>{option.label}</option>
                })
              }
            </Form.Select>
          </div>
          <div className="col-8">
            <Form.Control
              type="text"
              value={paymentNumber.value}
              onBlur={handleBlur}
              onChange={handleNumberChange}
              placeholder={`${paymentMethod.value.replace('口座', '')}番号を入力してください。`}
              aria-label="識別番号"
              isInvalid={paymentNumber.errorMessage !== ''}
              maxLength={paymentMethod.value === '予納台帳' ? 6 : (paymentMethod.value === '口座振替' ? 8 : 10)}
            />
          </div>
        </div>
        {paymentMethod.errorMessage !== '' ? <div className="text-end text-danger small">{paymentMethod.errorMessage}</div> : ''}
        {paymentNumber.errorMessage !== '' ? <div className="text-end text-danger small">{paymentNumber.errorMessage}</div> : ''}
      </div>
    </Form.Group>
  );
}

export default PaymentForm;